import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';

import './pages/styles.css';
import './pages/login/login.css';
import './bootstrap-utils.css';

import './pages/confirmation/confirmation.css';
import './pages/phone_confirmation/confirmation.css';
import './pages/schedule/schedule.css';
import './pages/registration/registration.css';
import './pages/phone/login.css';
import './pages/already_scheduled/scheduled.css';
import './pages/contact/contact.css';
import './pages/contact-error/contact.css';
import './pages/follow-up/contact.css';
import './component/waiting-for-crm-comp/waiting-for-crm.css';
import './pages/form-complete/contact.css';
import './pages/registration/registration.css';
import './component/contact-form/contact-form.css';
import './component/email-comp/google-score.css';
import './assets/comStyles.css';
import 'react-calendar/dist/Calendar.css';
import './custom-components/Calendar/styles.css';
import './component/custom-check-box/custom-check-box.css';
import './component/drop-downs/drop-downs.css';
import './component/consolidated-comp/single-step-form.css';
import './extra-css-tags.css';
import './component/circle-check.css';
import './component/call-center-modal/CallCenterErrorModal.css';
import './component/floating-input/floating-input.css';
import './component/zip-first-comp/zip-first.css';
import './component/zip-first-schedule-comp/zip-first-schedule.css';
import Spinner from './component/Spinner';
import { jwtToken } from './context';
import { CallCenterMode, TruvolvEnabled } from './context/style-data';
import useCallCenterErrorModal from './component/call-center-modal/useCallCenterErrorModal';
import CallCenterErrorModal from './component/call-center-modal/CallCenterErrorModal';
import useIframeAutoResize from './hooks/use-iframe-auto-resize';
import { useTruvolvSteps } from './hooks/use-truvolv-steps';
import { updateFormViewing, submitTruvolvFormStart } from './services/apt-page-api.service';
import { onFirstInteraction } from './util/first-interaction';

// Runs the route-driven Truvolv step tracker. Must live inside <Router> to use useLocation.
const TruvolvStepTracker = () => {
  useTruvolvSteps();
  return null;
};

const Login = lazy(() => import('./pages/login'));
const Schedule = lazy(() => import('./pages/schedule'));
const PersonalInfo = lazy(() => import('./pages/registration'));
const Scheduled = lazy(() => import('./pages/scheduled'));
const AlreadyScheduled = lazy(() => import('./pages/already_scheduled'));
const Contact = lazy(() => import('./pages/contact'));
const ContactError = lazy(() => import('./pages/contact-error'));
const UnsupportedPostalCode = lazy(() => import('./pages/unsupported-postal-code'));
const FollowUp = lazy(() => import('./pages/follow-up'));
const WaitingForCrm = lazy(() => import('./pages/waiting-for-crm'));
const FormComplete = lazy(() => import('./pages/form-complete'));
const SelectProduct = lazy(() => import('./pages/select-product'));
const EnterHomeYear = lazy(() => import('./pages/enter-home-year'));
const SelectHearAbout = lazy(() => import('./pages/select-hear-about'));
const EnterDetails = lazy(() => import('./pages/enter-details'));
const EnterPostApptDetails = lazy(() => import('./pages/enter-post-appt-details'));
const AppointmentNotes = lazy(() => import('./pages/appointment-notes'));
const SelectStaff = lazy(() => import('./pages/select-staff'));
const ConsolidatedForm = lazy(() => import('./pages/consolidated-form'));
const BasicHorizontalForm = lazy(() => import('./pages/basic-horizontal-form'));
const ZipFirst = lazy(() => import('./pages/zip-first'));
const ZipFirstSchedule = lazy(() => import('./pages/zip-first-schedule'));
const ContactFirst = lazy(() => import('./pages/contact-first'));

const App = () => {
  const { errorData, isOpen, toggle } = useCallCenterErrorModal();

  // Auto-resize iframe based on content height
  useIframeAutoResize();

  useEffect(() => {
    const interval = setInterval(() => {
      if (jwtToken && jwtToken.trustedForm) {
        const loadTrustedFormScript = () => {
          const tfScript = document.createElement('script');
          const parentUrl = jwtToken.parentUrl || window.location.href;
          tfScript.src = `https://api.trustedform.com/trustedform.js?parent_url=${encodeURIComponent(parentUrl)}`;
          tfScript.async = true;
          document.body.appendChild(tfScript);
          return () => {
            document.body.removeChild(tfScript);
          };
        };

        loadTrustedFormScript();
        clearInterval(interval);
      } else if (jwtToken && jwtToken.token) {
        clearInterval(interval);
      }
    }, 200);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let heartbeatInterval;
    const handleAuthReady = () => {
      if (CallCenterMode()) {
        updateFormViewing();
        heartbeatInterval = setInterval(updateFormViewing, 10_000);
      }
    };
    window.addEventListener('hvs:auth-ready', handleAuthReady);
    return () => {
      window.removeEventListener('hvs:auth-ready', handleAuthReady);
      clearInterval(heartbeatInterval);
    };
  }, []);

  // Fires the Truvolv "form started" beacon exactly once per session, on the user's first
  // interaction with any page — shared detector so a click that never moves focus still counts.
  // The backend also dedups per oneTimeToken, so a re-fire here is harmless.
  useEffect(() => {
    let removeInteractionListeners = null;
    const handleAuthReady = () => {
      if (!removeInteractionListeners && TruvolvEnabled()) {
        removeInteractionListeners = onFirstInteraction(submitTruvolvFormStart);
      }
    };
    window.addEventListener('hvs:auth-ready', handleAuthReady);
    return () => {
      window.removeEventListener('hvs:auth-ready', handleAuthReady);
      removeInteractionListeners?.();
    };
  }, []);

  return (
    <div>
      <Router>
        <TruvolvStepTracker />
        <Suspense
          fallback={
            <div>
              {' '}
              <Spinner style={{ width: '2rem', height: '2rem' }} color="light">
                &nbsp;
              </Spinner>
            </div>
          }>
          <Switch>
            <Route exact path="/" component={Login} />
            <Route exact path="/consolidated-form" component={ConsolidatedForm} />
            <Route exact path="/basic-horizontal-form" component={BasicHorizontalForm} />
            <Route exact path="/select-product" component={SelectProduct} />
            <Route exact path="/set-home-year" component={EnterHomeYear} />
            <Route exact path="/select-hear-about" component={SelectHearAbout} />
            <Route exact path="/select-staff" component={SelectStaff} />
            <Route exact path="/person-info" component={PersonalInfo} />
            <Route exact path="/enter-details" component={EnterDetails} />
            <Route exact path="/enter-post-appt-details" component={EnterPostApptDetails} />
            <Route exact path="/appointment-notes" component={AppointmentNotes} />
            <Route exact path="/schedule" component={Schedule} />
            <Route exact path="/appointment-status" component={Scheduled} />
            <Route exact path="/already-scheduled" component={AlreadyScheduled} />
            <Route exact path="/contact" component={Contact} />
            <Route exact path="/follow-up" component={FollowUp} />
            <Route exact path="/waiting-for-crm" component={WaitingForCrm} />
            <Route exact path="/form-complete" component={FormComplete} />
            <Route exact path="/contact-error" component={ContactError} />
            <Route exact path="/unsupported-postal-code" component={UnsupportedPostalCode} />
            <Route exact path="/zip-first" component={ZipFirst} />
            <Route exact path="/zip-first-schedule" component={ZipFirstSchedule} />
            <Route exact path="/contact-first" component={ContactFirst} />
          </Switch>
        </Suspense>
      </Router>
      <CallCenterErrorModal isOpen={isOpen} toggle={toggle} errorData={errorData} />
    </div>
  );
};

export default App;
